import { useContext, useMemo } from 'react';
import { AuthenticationContext } from '../../homeModules/auth/contexts/AuthenticationContext';
import { User } from '../../homeModules/auth/model';
import { getSelectedLocation, CorporateLocation } from './locationStore';

// Per-module access rule. `moduleValue` is the user.modules entry that grants admin (tier > 2) and
// toggle control; omit it for modules everyone controls. `pin` is how NON-admins resolve location;
// `default` is the first-load fallback before any toggle has been used this session.
type ModuleRule = { moduleValue?: string; pin: 'account' | 'tampa' | 'toggle'; default: CorporateLocation };

export const MODULE_RULES: Record<'warehouse' | 'collections' | 'chiller' | 'projectCommunications', ModuleRule> = {
  warehouse: { pin: 'toggle', default: 'pittsburgh' },
  projectCommunications: { pin: 'toggle', default: 'pittsburgh' },
  collections: { moduleValue: 'collections', pin: 'account', default: 'tampa' },
  chiller: { moduleValue: 'chiller_logs', pin: 'tampa', default: 'tampa' },
};

export type LocationModule = keyof typeof MODULE_RULES;

/**
 * Whether this user controls the location toggle for a module — i.e. whether the toggle should be
 * shown for them (UserTopNavigation) and whether this hook should follow it. One source of truth so
 * those two answers can never drift: true for admins, and for modules with no admin gate (everyone).
 */
export const followsLocationToggle = (module: LocationModule, user: User | null | undefined): boolean => {
  const rule = MODULE_RULES[module];
  return !rule.moduleValue || !!user?.modules?.some((m) => m.value === rule.moduleValue && parseInt(m.tier, 10) > 2);
};

// Which location module a route belongs to, or null for routes with no location toggle.
export const moduleForPath = (pathname: string): LocationModule | null => {
  if (pathname.includes('/warehouse')) return 'warehouse';
  if (pathname.includes('/project_communications')) return 'projectCommunications';
  if (pathname.includes('/chiller_logs')) return 'chiller';
  if (pathname.includes('/collections')) return 'collections';
  return null;
};

/**
 * The active corporate location for the current user, for a given module. Toggle-followers get the
 * global toggle value (falling back to the module's first-load default); non-admins are pinned per
 * the module rule — Collections to their account city (null if none), Chiller to Tampa.
 */
export const useCorporateLocation = (module: LocationModule): CorporateLocation | null => {
  const { user } = useContext(AuthenticationContext);

  return useMemo(() => {
    const rule = MODULE_RULES[module];
    const def = rule.default;

    if (followsLocationToggle(module, user)) return getSelectedLocation(def);
    if (rule.pin === 'account') return (user?.collections_location as CorporateLocation) ?? null;
    if (rule.pin === 'tampa') return 'tampa';
    return getSelectedLocation(def);
  }, [module, user]);
};
